import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Clock, Scissors, Volume2, Leaf, Check, Wrench, ShieldCheck, MapPin, Award } from "lucide-react";
import aboutTeam from "@/assets/about-team.png";
import aboutVans from "@/assets/about-vans.png";
import aboutShowroom from "@/assets/about-showroom.png";

const VALUES = [
  { icon: Clock, title: "Time saving", copy: "Reclaim your weekends — let the robot do the work." },
  { icon: Scissors, title: "Perfect cut", copy: "Consistent, healthy lawn with regular mulching." },
  { icon: Volume2, title: "Quiet operation", copy: "Low-noise motors that won't disturb you or neighbours." },
  { icon: Leaf, title: "Eco-friendly", copy: "Zero emissions and low energy consumption." },
];

const STATS = [
  { value: "2021", label: "Serving Ireland since" },
  { value: "Hundreds", label: "Mowers installed" },
  { value: "1000s", label: "Service & repair jobs" },
  { value: "Nationwide", label: "Coverage across Ireland" },
];

const DOMESTIC = [
  "Professional site assessment",
  "Expert boundary configuration & installation",
  "Complete setup and optimisation",
  "Training and demonstration",
  "Ongoing maintenance plans",
  "Full warranty support",
];

const WHY = [
  { icon: Award, title: "Specialist expertise", copy: "We focus solely on robotic mowers and line marking — installation, service and repair across all major brands." },
  { icon: ShieldCheck, title: "Authorised & trusted", copy: "Working with leading manufacturers including Mammotion, Roborock, Stiga and FJDynamics." },
  { icon: Wrench, title: "In-house workshop", copy: "A fully-equipped workshop for diagnostics, servicing and repairs — with genuine parts." },
  { icon: MapPin, title: "Based in Co. Meath", copy: "Located in Fyanstown, Kells, serving homes, clubs and commercial sites nationwide." },
];

export default function About() {
  return (
    <div>
      {/* Hero */}
      <section className="relative bg-brand-ink text-white overflow-hidden">
        <div className="absolute inset-0"><img src={aboutVans} alt="" className="h-full w-full object-cover opacity-30" /><div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/90 to-brand-ink/50" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="font-mono-spec text-xs uppercase tracking-widest text-white">About Us</span>
            <h1 className="mt-3 font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Ireland's robotic lawnmower specialists</h1>
            <p className="mt-5 max-w-xl text-lg text-white/80">Founded by John Gorman, Robotic Lawnmower Experts installs, services and repairs robotic mowers for homes, clubs and commercial sites across Ireland — backed by genuine local expertise.</p>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10">
            <img src={aboutTeam} alt="John Gorman and the Robotic Lawnmower Experts team beside a branded van" className="w-full h-80 lg:h-[26rem] object-cover object-[center_35%]" data-testid="about-team-image" />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-display font-extrabold text-3xl sm:text-4xl">{s.value}</div>
              <div className="mt-1 text-sm text-white/70">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-brand-navy/10 order-last lg:order-first">
          <img src={aboutShowroom} alt="Robotic mower showroom display featuring Mammotion and Roborock models" className="w-full h-96 object-cover" data-testid="about-showroom-image" />
        </div>
        <div>
          <span className="font-mono-spec text-xs uppercase tracking-widest text-brand-navy">Our story</span>
          <h2 className="mt-3 font-display font-bold tracking-tight text-2xl sm:text-3xl text-brand-ink">Built on expertise and genuine customer care</h2>
          <div className="mt-5 space-y-4 text-muted-foreground leading-relaxed">
            <p><span className="font-semibold text-brand-ink">John Gorman</span> is the owner and founder of Robotic Lawnmower Experts, a company specialising in the service, repair and installation of robotic lawnmowers. Since entering the industry in 2021, he has built a strong reputation for reliable, high-quality workmanship and exceptional customer service.</p>
            <p>With hands-on experience installing hundreds of robotic lawnmowers and carrying out thousands of service and repair jobs, John has developed extensive industry expertise. He has also trained an expert team to uphold the same high standards of focus, professionalism and quality workmanship.</p>
            <p>Driven by strong customer-focused values, Robotic Lawnmower Experts is committed to providing professional, efficient service that ensures optimal mower performance and complete customer satisfaction — for every home, club and commercial site we work with.</p>
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="bg-white border-y border-brand-navy/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Why choose Robotic Lawnmower Experts</h2>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">Specialist robotic mowing and line-marking knowledge, professional installation and dependable aftercare — all from one trusted Irish team.</p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w) => (
              <div key={w.title} className="rounded-2xl border border-brand-navy/10 p-6">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-navy text-white"><w.icon className="h-5 w-5" /></span>
                <h3 className="mt-4 font-display font-semibold text-lg text-brand-navy">{w.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{w.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits of robotic mowing */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Benefits of robotic mowing</h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v) => (
            <div key={v.title} className="rounded-2xl border border-brand-navy/10 bg-white p-6">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-navy text-white"><v.icon className="h-5 w-5" /></span>
              <h3 className="mt-4 font-display font-semibold text-lg text-brand-navy">{v.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{v.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Domestic services */}
      <section className="bg-white border-y border-brand-navy/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-brand-navy/10"><img src={aboutVans} alt="Robotic Lawnmower Experts branded vans and robotic mower range" className="w-full h-80 object-cover" data-testid="about-vans-image" /></div>
          <div>
            <span className="font-mono-spec text-xs uppercase tracking-widest text-brand-navy">Domestic services</span>
            <h2 className="mt-3 font-display font-bold tracking-tight text-2xl sm:text-3xl text-brand-ink">From assessment to ongoing support</h2>
            <p className="mt-4 text-muted-foreground">We handle everything from initial site assessment to ongoing support, ensuring your lawn stays perfectly maintained year-round.</p>
            <ul className="mt-6 space-y-2">{DOMESTIC.map((d) => <li key={d} className="flex items-start gap-2 text-brand-ink"><Check className="h-4 w-4 text-brand-navy mt-1 shrink-0" /> {d}</li>)}</ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild className="bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold"><Link to="/book-assessment" data-testid="about-book-assessment">Book a Site Assessment</Link></Button>
              <Button asChild variant="outline" className="border-brand-navy/30 text-brand-navy hover:bg-brand-navy hover:text-white"><Link to="/domestic">Browse our mowers</Link></Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="rounded-3xl bg-brand-ink text-white p-8 md:p-12 text-center">
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl">Talk to Ireland's robotic mowing specialists</h2>
          <p className="mt-3 text-white/80 max-w-2xl mx-auto">Whether it's a garden, a sports pitch or a commercial site, we'll advise on the right solution and look after it for the long term.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="h-12 px-7 bg-white hover:bg-white/90 text-brand-navy font-semibold"><Link to="/contact" data-testid="about-contact">Contact us</Link></Button>
            <Button asChild size="lg" variant="outline" className="h-12 px-7 border-white/30 bg-white/5 text-white hover:bg-white hover:text-brand-navy"><Link to="/service">Service & Repairs</Link></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
